export type SearchFilters = {
    description?: string;
    title?: string;
    ticketNumber?: string;
    statuses?: string[];
    assignee?: string;
    unassigned?: boolean;
    requestorEmail?: string;
    createdAfter?: string;
    createdBefore?: string;
    updatedAfter?: string;
    updatedBefore?: string;
};

/** Field-only advanced filters (no status / assignee — those live in the overview toolbar). */
export type AdvancedFieldFilters = Pick<
    SearchFilters,
    | 'description'
    | 'title'
    | 'ticketNumber'
    | 'requestorEmail'
    | 'createdAfter'
    | 'createdBefore'
    | 'updatedAfter'
    | 'updatedBefore'
>;

export function hasActiveSearchFilters(filters?: SearchFilters | null): boolean {
    if (!filters) return false;
    if (filters.description?.trim()) return true;
    if (filters.title?.trim()) return true;
    if (filters.ticketNumber?.trim()) return true;
    if (filters.statuses && filters.statuses.length > 0) return true;
    if (filters.assignee) return true;
    if (filters.unassigned) return true;
    if (filters.requestorEmail?.trim()) return true;
    if (filters.createdAfter || filters.createdBefore) return true;
    if (filters.updatedAfter || filters.updatedBefore) return true;
    return false;
}

export function hasActiveAdvancedFieldFilters(filters?: AdvancedFieldFilters | null): boolean {
    if (!filters) return false;
    return hasActiveSearchFilters({
        description: filters.description,
        title: filters.title,
        ticketNumber: filters.ticketNumber,
        requestorEmail: filters.requestorEmail,
        createdAfter: filters.createdAfter,
        createdBefore: filters.createdBefore,
        updatedAfter: filters.updatedAfter,
        updatedBefore: filters.updatedBefore,
    });
}

export function filtersToQuery(filters?: SearchFilters | null): AnyObj {
    if (!filters) return {};

    const query: AnyObj = {};
    if (filters.description?.trim())
        query.description = filters.description.trim();
    if (filters.title?.trim())
        query.title = filters.title.trim();
    if (filters.ticketNumber?.trim())
        query.ticket_number = filters.ticketNumber.trim();
    if (filters.statuses && filters.statuses.length > 0)
        query['progress.status'] = filters.statuses.join(',');
    if (filters.unassigned)
        query.unassigned = true;
    else if (filters.assignee)
        query.assignee = filters.assignee;
    if (filters.requestorEmail?.trim())
        query.requestor_email = filters.requestorEmail.trim();
    if (filters.createdAfter)
        query.created_after = filters.createdAfter;
    if (filters.createdBefore)
        query.created_before = filters.createdBefore;
    if (filters.updatedAfter)
        query.updated_after = filters.updatedAfter;
    if (filters.updatedBefore)
        query.updated_before = filters.updatedBefore;

    return query;
}

const ADVANCED_URL_KEYS = [
    'title',
    'ticket_number',
    'description',
    'requestor_email',
    'created_after',
    'created_before',
    'updated_after',
    'updated_before',
] as const;

export function advancedFieldFiltersFromParams(params: URLSearchParams): AdvancedFieldFilters {
    const filters: AdvancedFieldFilters = {};
    const title = params.get('title');
    const ticketNumber = params.get('ticket_number');
    const description = params.get('description');
    const requestorEmail = params.get('requestor_email');
    const createdAfter = params.get('created_after');
    const createdBefore = params.get('created_before');
    const updatedAfter = params.get('updated_after');
    const updatedBefore = params.get('updated_before');

    if (title) filters.title = title;
    if (ticketNumber) filters.ticketNumber = ticketNumber;
    if (description) filters.description = description;
    if (requestorEmail) filters.requestorEmail = requestorEmail;
    if (createdAfter) filters.createdAfter = createdAfter;
    if (createdBefore) filters.createdBefore = createdBefore;
    if (updatedAfter) filters.updatedAfter = updatedAfter;
    if (updatedBefore) filters.updatedBefore = updatedBefore;

    return filters;
}

export function appendAdvancedFieldFiltersToParams(
    params: URLSearchParams,
    filters?: AdvancedFieldFilters | null,
): void {
    ADVANCED_URL_KEYS.forEach((key) => params.delete(key));
    if (!filters) return;

    const query = filtersToQuery(filters);
    Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        params.set(key, String(value));
    });
}

/** Build `/changes?...` path for the advanced-search entry from the quick-search modal. */
export function buildChangesOverviewPath(query?: string, openAdvanced = true): string {
    const params = new URLSearchParams();
    const trimmed = query?.trim();
    if (trimmed) params.set('q', trimmed);
    if (openAdvanced) params.set('advanced', '1');
    const qs = params.toString();
    return qs ? `/changes?${qs}` : '/changes';
}
